import { confirmationToast, errorToast } from "@/components/ui/toast.tsx";
import { useCurrentUserContext } from "@/hooks/useUser.tsx";
import { api } from "@/lib/http.ts";
import * as Sentry from "@sentry/react";
import { isAxiosError } from "axios";
import { useCallback, useState } from "react";

export const useIncrementalSync = () => {
  const { userContext } = useCurrentUserContext();
  const userId = userContext?.user.id;
  const [isSyncing, setIsSyncing] = useState(false);

  // Stable identity matters: App.tsx registers this as the system.sync hotkey
  // handler, which re-registers whenever the handler changes.
  const handleIncrementalSync = useCallback(async () => {
    setIsSyncing(true);
    try {
      await api.post("/api/a/users/force_sync", { userId });
      // "started", not "synced": the endpoint only enqueues an account sync job
      // and returns, so the work is still ahead of us. This is the hotkey path's
      // only feedback — ⌘I has no button to spin, so without it a successful
      // sync is indistinguishable from a dead keybinding.
      confirmationToast("Sync started");
    } catch (error) {
      // Swallowing the rejection matters for the hotkey path too, which would
      // otherwise float an unhandled rejection. On a 401 the http interceptor
      // is already redirecting to /auth — a "Sync failed" toast on the way out
      // would misreport a session expiry.
      Sentry.captureException(error, { tags: { feature: "incremental-sync" } });
      if (!(isAxiosError(error) && error.response?.status === 401)) {
        errorToast("Sync failed. Please try again.");
      }
    } finally {
      // Without this, a failed request leaves the button stuck in its syncing
      // state forever — the flag is the only signal the UI has.
      setIsSyncing(false);
    }
  }, [userId]);

  return { isSyncing, handleIncrementalSync };
};
